import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Divider,
  alpha,
} from "@mui/material";
import { useSelector } from "react-redux";
import web3 from "../../Assets/Images/web3-wallet-banner.png";
import { QRCodeCanvas } from "qrcode.react";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import { ContainedButton } from "../../Common/Buttons";

const WebThreeWallet = ({ data, Themecolor = "Dark", openQrCodePopup }) => {
  const { brokerBasicInfo, btncolor } = useSelector((state) => state);
  const [qrCodeModal, setQrCodeModal] = useState(false);

  const qrString = `otpauth://totp/${localStorage.getItem(
    "uuid"
  )}?action=OpenWeb3Wallet&issuer=${brokerBasicInfo.exchange}`;

  const features = [
    {
      icon: <ShieldOutlinedIcon sx={{ fontSize: 20 }} />,
      title: "Non-Custodial Security",
      desc: "You own your private keys. No third-party control over your assets.",
    },
    {
      icon: <LinkOutlinedIcon sx={{ fontSize: 20 }} />,
      title: "Multi-Chain Support",
      desc: "Connect to Ethereum, BNB Chain, Polygon and more from one wallet.",
    },
    {
      icon: <DevicesOutlinedIcon sx={{ fontSize: 20 }} />,
      title: "Mobile Ready",
      desc: "Scan the QR code from your iOS or Android app to get started instantly.",
    },
    {
      icon: <QrCode2OutlinedIcon sx={{ fontSize: 20 }} />,
      title: "Easy Onboarding",
      desc: "One QR scan links your exchange account to your Web3 wallet securely.",
    },
  ];

  return (
    <Box sx={pageWrapper}>
      {/* ── Hero Banner ── */}
      <Box
        sx={{
          ...heroBanner,
          background: `linear-gradient(135deg, ${alpha(
            btncolor || "#1976d2",
            0.95
          )} 0%, ${alpha(btncolor || "#1976d2", 0.72)} 100%)`,
        }}
      >
        {/* Decorative circles */}
        <Box sx={heroDecorCircleLg} />
        <Box sx={heroDecorCircleSm} />

        <Box sx={heroLayout}>
          {/* Left: text */}
          <Box sx={heroLeft}>
            <Box sx={heroBadge}>
              <AccountBalanceWalletOutlinedIcon
                sx={{ fontSize: 13, color: "#fff" }}
              />
              <Typography sx={heroBadgeText}>Web3 Wallet</Typography>
            </Box>

            <Typography sx={heroTitle}>
              {brokerBasicInfo?.exchange || ""} Web3 Wallet
            </Typography>

            <Typography sx={heroSubtitle}>
              Secure your world of Web3 — manage assets, connect to dApps and
              explore decentralised finance, all from one place.
            </Typography>

            <ContainedButton
              variant="contained"
              onClick={() => setQrCodeModal(true)}
              sx={heroCTA}
            >
              <QrCode2OutlinedIcon sx={{ fontSize: 18 }} />
              Get Started
            </ContainedButton>
          </Box>

          {/* Right: image */}
          <Box sx={heroRight}>
            <Box
              component="img"
              src={web3}
              alt="Web3 Wallet"
              sx={heroImage}
            />
          </Box>
        </Box>
      </Box>

      {/* ── Main Card ── */}
      <Box sx={mainCard}>
        {/* Section header */}
        <Box sx={sectionHeader}>
          <Box
            sx={{
              ...sectionAccent,
              background: `linear-gradient(180deg, ${alpha(
                btncolor || "#1976d2",
                1
              )} 0%, ${alpha(btncolor || "#1976d2", 0.35)} 100%)`,
            }}
          />
          <Box>
            <Typography sx={sectionTitle}>Why use the Web3 Wallet?</Typography>
            <Typography sx={sectionSub}>
              Everything you need to navigate decentralised finance, secured by
              your own keys
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "rgba(125,125,125,0.13)", mb: 3 }} />

        {/* Feature grid */}
        <Box sx={featureGrid}>
          {features.map((f, i) => (
            <Box key={i} sx={featureCard}>
              <Box
                sx={{
                  ...featureIconWrap,
                  backgroundColor: alpha(btncolor || "#1976d2", 0.1),
                  color: btncolor,
                }}
              >
                {f.icon}
              </Box>
              <Box>
                <Typography sx={featureTitle}>{f.title}</Typography>
                <Typography sx={featureDesc}>{f.desc}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Divider sx={{ borderColor: "rgba(125,125,125,0.13)", mt: 3, mb: 3 }} />

        {/* CTA row */}
        <Box sx={ctaRow}>
          <Box>
            <Typography sx={ctaTitle}>Ready to dive in?</Typography>
            <Typography sx={ctaSub}>
              Scan the QR code from your{" "}
              {brokerBasicInfo?.exchange || "exchange"} mobile app to link your
              Web3 Wallet in seconds.
            </Typography>
          </Box>
          <ContainedButton
            variant="contained"
            onClick={() => setQrCodeModal(true)}
            sx={ctaBtn}
          >
            <QrCode2OutlinedIcon sx={{ fontSize: 18 }} />
            Scan QR Code
          </ContainedButton>
        </Box>
      </Box>

      {/* ── QR Code Dialog ── */}
      <Dialog
        open={qrCodeModal}
        onClose={() => setQrCodeModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: dialogPaper }}
      >
        <DialogTitle sx={dialogTitleStyle}>
          Scan the QR Code
          <IconButton
            onClick={() => setQrCodeModal(false)}
            size="small"
            sx={dialogCloseBtn}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />

        <DialogContent sx={dialogContentStyle}>
          <Typography sx={dialogBodyText}>
            Scan the QR code using your{" "}
            <strong>{brokerBasicInfo?.exchange}</strong> Android or iOS app.
          </Typography>
          <Typography sx={{ ...dialogBodyText, mb: 3 }}>
            Go to your app's home page and find the QR scanner option in the
            header / tab-bar.
          </Typography>

          <Box sx={qrWrapper}>
            <QRCodeCanvas value={qrString} size={220} level="M" includeMargin={true} />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

// Hero
const heroBanner = {
  borderRadius: "20px",
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 12px 40px rgba(0,0,0,0.22)",
};

const heroDecorCircleLg = {
  position: "absolute",
  width: 480,
  height: 480,
  borderRadius: "50%",
  backgroundColor: "rgba(255,255,255,0.06)",
  right: -100,
  top: -140,
  pointerEvents: "none",
};

const heroDecorCircleSm = {
  position: "absolute",
  width: 220,
  height: 220,
  borderRadius: "50%",
  backgroundColor: "rgba(255,255,255,0.05)",
  left: -50,
  bottom: -70,
  pointerEvents: "none",
};

const heroLayout = {
  display: "flex",
  alignItems: "center",
  gap: { xs: 2, md: 4 },
  px: { xs: 3, md: 5 },
  py: { xs: 3.5, md: 4 },
  position: "relative",
  zIndex: 1,
};

const heroLeft = {
  flex: 1,
  minWidth: 0,
};

const heroBadge = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.6,
  backgroundColor: "rgba(255,255,255,0.15)",
  border: "1px solid rgba(255,255,255,0.22)",
  borderRadius: "20px",
  px: 1.5,
  py: 0.4,
  mb: 1.75,
};

const heroBadgeText = {
  fontSize: "0.7rem",
  fontWeight: 700,
  color: "#fff",
  textTransform: "uppercase",
  letterSpacing: "0.07em",
};

const heroTitle = {
  fontSize: { xs: "1.5rem", md: "2rem" },
  fontWeight: 800,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.02em",
  mb: 1,
};

const heroSubtitle = {
  fontSize: { xs: "0.85rem", md: "0.95rem" },
  color: "rgba(255,255,255,0.82)",
  lineHeight: 1.6,
  mb: 3,
  maxWidth: 520,
};

const heroCTA = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  height: 46,
  px: 3,
  borderRadius: "12px",
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.92rem",
  backgroundColor: "rgba(255,255,255,0.18)",
  backdropFilter: "blur(6px)",
  border: "1px solid rgba(255,255,255,0.3)",
  color: "#fff",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.28)",
  },
};

const heroRight = {
  flexShrink: 0,
  display: { xs: "none", md: "flex" },
  alignItems: "center",
  justifyContent: "flex-end",
};

const heroImage = {
  width: 260,
  filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.28))",
};

// Main card
const mainCard = {
  backgroundColor: "var(--body-color-less)",
  borderRadius: "16px",
  border: "1px solid rgba(125,125,125,0.15)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.06), 0 10px 25px rgba(0,0,0,0.06)",
  overflow: "hidden",
  p: { xs: 2, md: 3 },
};

const sectionHeader = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.5,
  mb: 2.5,
};

const sectionAccent = {
  width: 4,
  borderRadius: "4px",
  alignSelf: "stretch",
  minHeight: 36,
  flexShrink: 0,
};

const sectionTitle = {
  fontSize: "1rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.3,
  mb: 0.3,
};

const sectionSub = {
  fontSize: "0.8rem",
  color: "var(--text-color)",
  opacity: 0.55,
  lineHeight: 1.5,
};

// Feature grid
const featureGrid = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
  gap: 2,
};

const featureCard = {
  display: "flex",
  alignItems: "flex-start",
  gap: 1.5,
  p: 2,
  borderRadius: "14px",
  border: "1px solid rgba(125,125,125,0.13)",
  backgroundColor: "var(--body-color-less2)",
  transition: "transform 0.18s ease, box-shadow 0.18s ease",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
  },
};

const featureIconWrap = {
  width: 40,
  height: 40,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const featureTitle = {
  fontSize: "0.88rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.3,
  mb: 0.4,
};

const featureDesc = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.6,
  lineHeight: 1.6,
};

// CTA row
const ctaRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  flexWrap: "wrap",
};

const ctaTitle = {
  fontSize: "0.95rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.3,
  mb: 0.4,
};

const ctaSub = {
  fontSize: "0.8rem",
  color: "var(--text-color)",
  opacity: 0.55,
  lineHeight: 1.55,
  maxWidth: 480,
};

const ctaBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.75,
  height: 44,
  px: 3,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.88rem",
  flexShrink: 0,
};

// Dialog
const dialogPaper = {
  borderRadius: "16px",
  minWidth: { xs: "90%", sm: 420 },
  boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
  overflow: "hidden",
  backgroundColor: "var(--popupbody-color)",
  color: "var(--text-color)",
};

const dialogTitleStyle = {
  textAlign: "center",
  fontWeight: 700,
  fontSize: "1.05rem",
  py: 2.25,
  color: "var(--text-color)",
  backgroundColor: "var(--popupheader-color)",
  position: "relative",
};

const dialogCloseBtn = {
  position: "absolute",
  right: 12,
  top: "50%",
  transform: "translateY(-50%)",
  color: "var(--text-color)",
  opacity: 0.6,
};

const dialogContentStyle = {
  backgroundColor: "var(--popupbody-color)",
  py: 3,
  px: 3,
  textAlign: "center",
};

const dialogBodyText = {
  fontSize: "0.85rem",
  color: "var(--text-color)",
  opacity: 0.75,
  lineHeight: 1.65,
  mb: 1,
};

const qrWrapper = {
  display: "inline-flex",
  p: 2,
  borderRadius: "14px",
  backgroundColor: "#fff",
  boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
};

export default WebThreeWallet;
